import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { PROXY } from "../../lib/utils";
import { toast } from "react-toastify";
import { FaSignInAlt } from "react-icons/fa";
import { BsArrowRepeat, BsCheckCircle, BsClipboard, BsEye, BsEyeSlash, BsKey } from "react-icons/bs";
import GoogleLoginButton from "../../components/GoogleLoginButton";
import TurnstileWidget from "../../components/TurnstileWidget";
import { registrationSiteKey } from "../../lib/turnstile";
import { apiErrorMessage, apiValidationMessage, passwordIssue, passwordMessage } from "../../lib/passwordPolicy";
import { generateSecurePassword } from "../../lib/passwordGenerator";

const Register = () => {
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [copyFeedback, setCopyFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [resetChallenge, setResetChallenge] = useState(0);
  const siteKey = registrationSiteKey(window.location.hostname, process.env.REACT_APP_TURNSTILE_SITE_KEY);

  // ✅ Validation schema with localization
  const schema = yup.object().shape({
    fullName: yup
      .string()
      .required(t("validation.name_required"))
      .min(3, t("validation.name_min"))
      .max(50, t("validation.name_max"))
      .matches(/^[A-Za-z\u0600-\u06FF]/, t("validation.name_start_letter")),
    email: yup
      .string()
      .email(t("validation.email_invalid"))
      .required(t("validation.email_required")),
    password: yup.string().test("password-policy", function (value) {
      const issue = passwordIssue(value);
      return issue ? this.createError({ message: passwordMessage(issue, t) }) : true;
    }),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password"), null], t("validation.password-match"))
      .required(t("validation.confirm-password-required")),
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    control,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    if (!copyFeedback) return undefined;
    const timeout = window.setTimeout(() => setCopyFeedback(""), 3000);
    return () => window.clearTimeout(timeout);
  }, [copyFeedback]);

  const fillGeneratedPassword = () => {
    const candidate = generateSecurePassword();
    setValue("password", candidate, { shouldDirty: true });
    setValue("confirmPassword", candidate, { shouldDirty: true });
    clearErrors(["password", "confirmPassword"]);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setCopyFeedback("");
    setGeneratedPassword(candidate);
  };

  const copyGeneratedPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setCopyFeedback("passwordCopied");
    } catch {
      setCopyFeedback("copyPasswordFailed");
    }
  };

  const submitHandler = async (data) => {
    if (!turnstileToken) return toast.error(t("registrationSecurityRequired"));
    setIsSubmitting(true);
    try {
      // Removed requestTeacherRole from the payload
      await axios.post(`${PROXY}/users/register`, {
        email: data.email,
        fullName: data.fullName,
        password: data.password,
        confirmPassword: data.confirmPassword,
        captchaToken: turnstileToken,
      });

      toast.success(t("registrationReady"));
      reset();
      setGeneratedPassword("");
      setCopyFeedback("");
    } catch (error) {
      if (!navigator.onLine) {
        toast.error(t("No-internet-connection-Please-check-your-network."));
      } else {
        const response = error.response?.data;
        response?.errors?.forEach((err) => {
          if (["fullName", "email", "password", "confirmPassword"].includes(err.path)) {
            setError(err.path, { type: "manual", message: apiValidationMessage(err, t) });
          }
        });
        toast.error(apiErrorMessage(response, t));
      }
    } finally {
      setIsSubmitting(false);
      setTurnstileToken("");
      setResetChallenge((value) => value + 1);
    }
  };

  return (
    <section
      className="flex min-h-screen items-center justify-center px-4 py-10 sm:px-6 sm:py-14 lg:py-20"
      dir={i18n.language === "en" ? "ltr" : "rtl"}
    >
      <form
        onSubmit={handleSubmit(submitHandler)}
        className="relative mx-auto flex w-full max-w-md flex-col rounded-2xl bg-white text-gray-700 shadow-xl shadow-gray-900/10"
      >
        {/* Header */}
        <div className="relative mx-5 -mt-6 mb-5 grid h-28 place-items-center overflow-hidden rounded-2xl bg-gradient-to-tr from-indigo-900 to-indigo-800 text-center text-white shadow-lg shadow-gray-900/20">
          <h3 className="text-3xl font-semibold leading-snug">
            {t("sign_up")}
            <p className="text-sm mt-2 tracking-wide">{t("app-name")}</p>
          </h3>
        </div>

        {/* Form Fields */}
        <div className="flex flex-col gap-4 px-6 pb-2 pt-1 sm:px-8">
          {/* Full Name */}
          <div
            dir={i18n.language === "en" ? "ltr" : "rtl"}
            className="relative h-11 w-full min-w-[200px] mb-3"
          >
            <input
              dir={i18n.language === "en" ? "ltr" : "rtl"}
              {...register("fullName")}
              className={`w-full h-full px-3 py-3 font-sans text-sm font-normal transition-all bg-transparent border rounded-md peer border-t-transparent text-blue-gray-700 outline outline-0 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50
                ${errors.fullName ? "focus:border-red-500" : "focus:border-gray-900"}`}
              placeholder=""
            />
            {errors.fullName && (
              <p className="text-xs text-red-500 px-1 my-1">
                {errors.fullName.message}
              </p>
            )}
            <label
              className={`before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight text-gray-500 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-transparent before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-transparent after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.1] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:!border-gray-900 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500
                ${errors.fullName ? "peer-focus:text-red-500 peer-focus:after:!border-red-500 before:border-blue-gray-200" : ""}`}
            >
              {t("form.your-full-name")}
            </label>
          </div>

          {/* Email */}
          <div
            dir={i18n.language === "en" ? "ltr" : "rtl"}
            className="relative h-11 w-full min-w-[200px] mb-3"
          >
            <input
              {...register("email")}
              type="email"
              name="email"
              autoComplete="email"
              dir={i18n.language === "en" ? "ltr" : "rtl"}
              className={`w-full h-full px-3 py-3 font-sans text-sm font-normal transition-all bg-transparent border rounded-md peer border-t-transparent text-blue-gray-700 outline outline-0 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50
                ${errors.email ? "focus:border-red-500" : "focus:border-gray-900"}`}
              placeholder=""
            />
            {errors.email && (
              <p className="text-xs text-red-500 px-1 my-1">
                {errors.email.message}
              </p>
            )}
            <label
              className={`before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight text-gray-500 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-transparent before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-transparent after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.1] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:!border-gray-900 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500
                ${errors.email ? "peer-focus:text-red-500 peer-focus:after:!border-red-500 before:border-blue-gray-200" : ""}`}
            >
              {t("form.your-email")}
            </label>
          </div>

          {/* Password with show/hide toggle */}
          <div
            dir={i18n.language === "en" ? "ltr" : "rtl"}
            className="relative h-11 w-full min-w-[200px] mb-3"
          >
            <Controller name="password" control={control} defaultValue="" render={({ field }) => (
              <input
                {...field}
                name="new-password"
                onChange={(event) => {
                  field.onChange(event.target.value);
                  if (generatedPassword) setGeneratedPassword("");
                  setCopyFeedback("");
                }}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                className={`w-full h-full pl-3 pr-20 py-3 font-sans text-sm font-normal transition-all bg-transparent border rounded-md peer border-t-transparent text-blue-gray-700 outline outline-0 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50
                  ${errors.password ? "focus:border-red-500" : "focus:border-gray-900"}`}
                placeholder=""
              />
            )} />
            <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
              <button type="button" onClick={fillGeneratedPassword} title={t("passwordGenerator.generateSecurePassword")} aria-label={t("passwordGenerator.generateSecurePassword")} className="flex h-8 w-8 items-center justify-center rounded text-indigo-800 hover:bg-indigo-50 hover:text-indigo-950 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <BsKey size={18} aria-hidden="true" />
              </button>
              <button type="button" className="flex h-8 w-8 items-center justify-center rounded text-indigo-800 hover:bg-indigo-50 hover:text-indigo-950 focus:outline-none focus:ring-2 focus:ring-indigo-500" aria-label={t(showPassword ? "passwordGenerator.hidePassword" : "passwordGenerator.showPassword")} onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? (
                  <BsEye size={18} aria-hidden="true" />
                ) : (
                  <BsEyeSlash size={18} aria-hidden="true" />
                )}
              </button>
            </span>
            {errors.password && (
              <p className="text-xs text-red-500 px-1 my-1">
                {errors.password.message}
              </p>
            )}
            <label
              className={`before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate pr-20 text-[11px] font-normal leading-tight text-gray-500 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-transparent before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-transparent after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.1] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:!border-gray-900 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500
                ${errors.password ? "peer-focus:text-red-500 peer-focus:after:!border-red-500 before:border-blue-gray-200" : ""}`}
            >
              {t("form.your-password")}
            </label>
          </div>

          {generatedPassword && (
            <div className="-mt-3 flex min-w-0 items-start gap-1 text-xs text-emerald-800">
              <BsCheckCircle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
              <p role="status" aria-live="polite" className="min-w-0 flex-1 leading-snug">
                {copyFeedback
                  ? t(`passwordGenerator.${copyFeedback}`)
                  : `${t("passwordGenerator.securePasswordGenerated")}. ${t("passwordGenerator.browserSaveHint")}`}
              </p>
              <button type="button" onClick={copyGeneratedPassword} title={t("passwordGenerator.copyPassword")} aria-label={t("passwordGenerator.copyPassword")} className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-indigo-800 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <BsClipboard size={16} aria-hidden="true" />
              </button>
              <button type="button" onClick={fillGeneratedPassword} title={t("passwordGenerator.generateAnother")} aria-label={t("passwordGenerator.generateAnother")} className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-indigo-800 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <BsArrowRepeat size={17} aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Confirm Password with show/hide toggle */}
          <div
            dir={i18n.language === "en" ? "ltr" : "rtl"}
            className="relative h-11 w-full min-w-[200px] mb-3"
          >
            <input
              {...register("confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              autoComplete="new-password"
              className={`w-full h-full px-3 py-3 font-sans text-sm font-normal transition-all bg-transparent border rounded-md peer border-t-transparent text-blue-gray-700 outline outline-0 placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50
                ${errors.confirmPassword ? "focus:border-red-500" : "focus:border-gray-900"}`}
              placeholder=""
            />
            <i
              className={`absolute ${i18n.language === "en" ? "right-4" : "left-4"} top-3`}
            >
              <button type="button" className="rounded text-indigo-800 hover:text-indigo-950 focus:outline-none focus:ring-2 focus:ring-indigo-500" aria-label={t(showConfirmPassword ? "passwordGenerator.hidePassword" : "passwordGenerator.showPassword")} onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                {showConfirmPassword ? (
                  <BsEye size={20} className="cursor-pointer" />
                ) : (
                  <BsEyeSlash size={20} className="cursor-pointer" />
                )}
              </button>
            </i>
            {errors.confirmPassword && (
              <p className="text-xs text-red-500 px-1 my-1">
                {errors.confirmPassword.message}
              </p>
            )}
            <label
              className={`before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none !overflow-visible truncate text-[11px] font-normal leading-tight text-gray-500 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-transparent before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-transparent after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[4.1] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-900 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:!border-gray-900 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500
                ${errors.confirmPassword ? "peer-focus:text-red-500 peer-focus:after:!border-red-500 before:border-blue-gray-200" : ""}`}
            >
              {t("form.confirm-password")}
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="px-6 pb-7 pt-3 sm:px-8 sm:pb-8">
          {siteKey ? (
            <TurnstileWidget siteKey={siteKey} onTokenChange={setTurnstileToken} resetSignal={resetChallenge} />
          ) : <p role="alert" className="mb-3 text-sm text-red-700">{t("registrationSecurityUnavailable")}</p>}
          <button
            type="submit"
            disabled={isSubmitting || !turnstileToken}
            className="block w-full rounded-lg bg-gradient-to-tr from-indigo-900 to-indigo-800 py-3 px-6 text-xs font-bold uppercase text-white shadow-md hover:shadow-lg transition-all"
          >
            {isSubmitting ? `${t("creating")}...` : t("sign_up")}
          </button>

          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-gray-400">
            <span className="h-px flex-1 bg-gray-200" />
            <span>or</span>
            <span className="h-px flex-1 bg-gray-200" />
          </div>

          <GoogleLoginButton label="Sign up quickly with Google" />

          <div className="flex justify-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white border border-gray-200 text-sm font-medium text-gray-800 shadow-sm hover:shadow-md transition-shadow duration-150 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 mt-6"
              aria-label={t("already-have-account")}
            >
              <FaSignInAlt className="text-indigo-600" />
              <span>{t("already-have-account")}</span>
            </Link>
          </div>
        </div>
      </form>
    </section>
  );
};

export default Register;
